import {Alert, Button, Card, Col, Divider, Dropdown, Grid, Empty, Form, Input, InputNumber, Menu, Modal, Popover, Row, Select, Space, Switch, Table, Tabs, Tag, Tooltip, Typography} from "antd";
import DatePicker from "../../core/components/DatePicker";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import ApiService from "../../core/service/ApiService";
import { v4 } from "uuid";
import Helper from "../../core/Helper";
import moment from "moment";
import FormDownload from "./FormDownload";
import { useModal } from "../../core/context/ModalContext";
import StateChanger from "./StateChanger";
import AppointmentFiles from "./files/AppointmentFiles";
import { DeleteOutlined, EyeOutlined, InfoCircleOutlined, SmallDashOutlined, FileTextOutlined, FormOutlined, ImportOutlined, PlusOutlined, SearchOutlined, DownOutlined, CalendarOutlined } from "@ant-design/icons";
import AppointmentSlotPicker from "./calendar/AppointmentSlotPicker";
import AppointmentTypeSelect from "./AppointmentTypeSelect";
import CurrencySelect from "../../core/components/CurrencySelect";
import { Currency } from "../Payment/MultiplePaymentApp";
import InstitutionCreateEdit from "../institution/InstitutionCreateEdit";
import AppointmentOperationSubItemsEdit from "./AppointmentOperationSubItemsEdit";
import { ColorDot } from "../../core/components/ColorDot";
import AppConfig from "../../core/service/AppConfig";
import Borcodeprinter from "./BarcodePrinter";
import { PriceQuoteStorage } from "../../core/service/PriceQuoteStorage";
import message from "../../core/service/NotificationService";
import { matchesSearch } from "../../core/utils/text-search";
import useAppointmentConsumables from "../consumable/useAppointmentConsumables";
import AppointmentConsumables from "../consumable/AppointmentConsumables";
import { needsConsumableConfirm, useConsumableConfirm } from "../consumable/ConsumableConfirmModal";

const { Text, Title } = Typography;

// Takvimdeki slot adımı (Calendar step={15}). Tek slot'a tıklamak ile gerçekten bir aralık
// sürüklemeyi ayırt etmek için kullanılıyor.
const CALENDAR_SLOT_MINUTES = 15;

const toDateTimeLocalString = (value) => {
    if (!value) return '';
    const m = moment.isMoment(value) ? value : moment(value);
    if (!m.isValid()) return '';
    return m.format('YYYY-MM-DDTHH:mm');
};

// Mobil için ayrı date + time input — datetime-local iOS Safari'de container'ı taşırıyordu
const DateTimeSplitInput = ({ value, onChange, disabled }) => {
    const m = value ? (moment.isMoment(value) ? value : moment(value)) : null;
    const dateStr = m && m.isValid() ? m.format('YYYY-MM-DD') : '';
    const timeStr = m && m.isValid() ? m.format('HH:mm') : '';

    const updateDate = (newDate) => {
        if (!newDate) {
            onChange && onChange(null);
            return;
        }
        const combined = moment(`${newDate} ${timeStr || '00:00'}`, 'YYYY-MM-DD HH:mm');
        onChange && onChange(combined.isValid() ? combined : null);
    };

    const updateTime = (newTime) => {
        const base = dateStr || moment().format('YYYY-MM-DD');
        const combined = moment(`${base} ${newTime || '00:00'}`, 'YYYY-MM-DD HH:mm');
        onChange && onChange(combined.isValid() ? combined : null);
    };

    return (
        <div style={{ display: 'flex', gap: 6, width: '100%', minWidth: 0 }}>
            <Input
                type="date"
                value={dateStr}
                onChange={(e) => updateDate(e.target.value)}
                disabled={disabled}
                size="middle"
                style={{ flex: 1, minWidth: 0 }}
            />
            <Input
                type="time"
                value={timeStr}
                onChange={(e) => updateTime(e.target.value)}
                disabled={disabled}
                size="middle"
                style={{ flex: '0 0 110px', minWidth: 0 }}
            />
        </div>
    );
};

// İşlem listesi masaüstünde tablo yerine kompakt satırlarla (renderCompactOperations), mobilde kartlarla çizilir.
// Eski tablo görünümüne dönmek için false.
const OPERATION_CARDS_ON_DESKTOP = true

export default function AppointmentDetailEdit({ appointmentId, onAppointmentChange, onSave, onDelete, onCancel, onPayment, onCheckedChange, onStateChanged }) {
    const formRef = useRef()
    const formOtherOperationRef = useRef();
    // Bitiş tarihi YALNIZCA işlem kümesi (işlem + süre) değiştiğinde yeniden hesaplanır.
    // Anamnez/not gibi alanları düzenleyip kaydetmek bitişi DEĞİŞTİRMEZ. Bu ref, işlem
    // imzasını tutup değişimi tespit eder (aşağıdaki effect). Yükleme sırasında baseline'a set edilir.
    const opsSigRef = useRef(null);
    const screens = Grid.useBreakpoint();
    const [doctors, setDoctors] = useState([]);
    const [institutions, setInstitutions] = useState([]);
    const { firmSettings } = useUser();
    const [operationList, setOperationList] = useState([]);
    // E-Nabız gönderim anahtarının (switch) aktifliğini hesaplamak için reaktif tip listesi
    const [appointmentTypeList, setAppointmentTypeList] = useState([])
    const { openModal, closeModal } = useModal()
    const { hasPermission, user } = useUser();
    const [currency, setCurrency] = useState('TRY')
    // hasQuote artık birim-aware: sadece bu randevunun birimine ait kayıt varsa true
    const [hasQuote, setHasQuote] = useState(false)
    // Bu birimle eşleşmeyen bir teklif varsa ufak uyarı için bilgi (null veya { unitName, firmName, opCount })
    const [otherUnitQuote, setOtherUnitQuote] = useState(null)
    const [operationSelectOpen, setOperationSelectOpen] = useState(false)
    const [operationSelectSearch, setOperationSelectSearch] = useState('')
    const [institutionSelectOpen, setInstitutionSelectOpen] = useState(false)
    const [operationPickerOpen, setOperationPickerOpen] = useState(false)
    const [operationPickerSearch, setOperationPickerSearch] = useState('')
    // Randevu saatini birimin takviminden seçme penceresi
    const [slotPickerOpen, setSlotPickerOpen] = useState(false)
    // Randevu hatırlatma bildirimi sistemde aktif mi (dropdown seçeneğini göster/gizle için).
    const [reminderEnabled, setReminderEnabled] = useState(false)
    const operationPickerSearchRef = useRef(null)
    // iOS mobil klavye primer'ı — user-gesture synchronous focus için
    const operationKeyboardPrimerRef = useRef(null)

    // Bildirim dropdown'ı için: hatırlatma sistemde aktif mi (hafif config, izin gerektirmez).
    useEffect(() => {
        ApiService.NotificationSettings.GetConsentConfig().then(res => {
            if (res.success) setReminderEnabled(!!res.data?.reminderEnabled)
        })
    }, [])

    // Modal açılınca real search input'a focus transfer et (primer'dan)
    useEffect(() => {
        if (!operationPickerOpen) return;
        const timer = setTimeout(() => {
            operationPickerSearchRef.current?.focus();
        }, 250);
        return () => clearTimeout(timer);
    }, [operationPickerOpen]);

    const openOperationPicker = () => {
        // Önce primer input'a synchronous focus → iOS klavyesi açılır
        try {
            operationKeyboardPrimerRef.current?.focus({ preventScroll: true });
        } catch { /* ignore */ }
        setOperationPickerSearch('');
        setOperationPickerOpen(true);
    };

    // Yüklü fiyat listesinin anahtarı: kurum/tür + işlem kümesi. Yalnız kimliğe bakınca aynı kurumlu ama başka
    // birimli randevuya geçişte (MR → Diş) eski birimin listesi kalıyor, yeni işlemlerin fiyatı NaN çıkıyordu.
    const institutionIdRef = useRef(null)
    const appointmentTypeIdRef = useRef(null)
    const [, setPriceListVersion] = useState(0)
    // İşlemler kartında aktif sekme: sekme çubuğunun sağındaki içerik (döviz / ekstra ekleme formu) buna göre
    const [operationsTab, setOperationsTab] = useState('operations')

    const priceList = useRef([])
    const appointmentTypePriceList = useRef([])
    const appointmentTypes = useRef([])

    const [appointment, setAppointment] = useState({
        doctor: {},
        institution: {},
        unit: {},
        appointmentOperations: [],
        appointmentOtherOperations: [],
        checkIn: false,
        sendEnabiz: true,
        enableNotification: true,
    })

    // Sarf satırları (işlem değildir: fiyatsız, toplama/POS'a girmez). Tamamlamada onay ekranı açılır.
    const consumables = useAppointmentConsumables({
        appointmentId,
        firmId: appointment?.firm?.id,
        unitId: appointment?.unit?.id,
        operations: appointment.appointmentOperations,
    })
    const [askConsumableConfirm, consumableConfirmModal] = useConsumableConfirm()
    // Kayıtta tamamlanmış randevuda sarf kilitlidir; kullanıcı durumu geri açarsa düzenlenebilir.
    const consumablesLocked = appointment.savedStateGroupId === 2 && (appointment.state?.stateGroup?.id ?? 2) === 2
    // Tamamlanmamış randevu "Tamamlandı" yapılıyorsa ve kullanılacak sarf varsa onay ekranı gerekir.
    const isCompletingState = (stateGroupId) => stateGroupId === 2 && appointment.savedStateGroupId !== 2

    const beforeStateChange = async (state) => {
        if (!isCompletingState(state?.stateGroup?.id) || !needsConsumableConfirm(consumables.pendingLines))
            return true
        return await askConsumableConfirm(consumables.pendingLines)
    }

    const editConsumableConfirmation = async () => {
        const result = await askConsumableConfirm(consumables.pendingLines)
        if (result) consumables.setConfirmations(result)
    }

    // Başka bir yerden (header butonu, bottom bar vb.) fiyat teklifi kaydedilince banner'ı güncelle.
    // Banner yalnızca bu randevunun birimine ait kayıt varsa görünür; farklı birime ait
    // kayıt varsa ufak uyarı için ayrı state.
    useEffect(() => {
        const evaluate = () => {
            const uId = appointment?.unit?.id;
            const forUnit = PriceQuoteStorage.getForUnit(uId);
            setHasQuote(!!forUnit);

            if (forUnit) {
                setOtherUnitQuote(null);
                return;
            }
            // Teklif çok birimli olabilir; bu birim dışındaki kalemleri özetle
            setOtherUnitQuote(uId ? PriceQuoteStorage.getOtherUnitsSummary(uId) : null);
        };
        evaluate();
        const unsubscribe = PriceQuoteStorage.subscribe(evaluate);
        return unsubscribe;
    }, [appointment?.unit?.id]);

    const loadAppointment = async (appointmentId) => {
        if (!appointmentId)
            return

        let _appointment = {
            doctor: {},
            institution: {},
            unit: {},
            appointmentOperations: [],
            appointmentOtherOperations: [],
            startDate: moment(),
            endDate: moment().add(15, 'minutes')
        }

        if (typeof appointmentId === 'object') {
            _appointment = {
                ..._appointment,
                unit: {
                    id: appointmentId.unitId,
                    name: appointmentId.unitName,
                },
                firm: {
                    id: appointmentId.firmId,
                },
                newRecord: true,
                startDate: appointmentId.startDate,
                endDate: appointmentId.endDate,
            }
        }
        else {
            const res = await ApiService.Appointment.GetAppointment(appointmentId)
            if (res.success) {
                _appointment = res.data
                _appointment.startDate = moment(new Date(_appointment.startDate))
                // Kayıtlı bitiş tarihini KORU (işlemlerin toplamına sıfırlama). Yalnızca
                // eksik/boşsa işlemlerden türet.
                _appointment.endDate = _appointment.endDate
                    ? moment(new Date(_appointment.endDate))
                    : calcEndDate(_appointment.appointmentOperations, _appointment.startDate)
                _appointment.newRecord = false;
                // Kayıtlı durum grubu: sarf kilidi ve tamamlama onayı buna göre (ekranda seçilen durum ayrı tutulur).
                _appointment.savedStateGroupId = _appointment.state?.stateGroup?.id;
                _appointment.appointmentOperations = _appointment.appointmentOperations.map(x => {
                    return {
                        ...x,
                        // Sunucudan gelen (kayıtlı) işlemler; APPOINTMENT_EDIT_OPERATION yetkisi olmayan kullanıcı bunları düzenleyemez/silemez.
                        persisted: true,
                        subItems: JSON.parse(x.subItems ?? '[]')
                    }
                });

            }
        }

        let _doctors = []
        let _institutions = []
        let _operations = []
        // Yeni kayıt akışında unit yalnızca {id, name} taşır; E-Nabız anahtarının doğru
        // değerlendirilmesi için birim seviyesindeki enabizEnabled bilgisini tamamla.
        // (Kayıtlı randevuda GetAppointment birimi zaten tam getirir → istek atılmaz.)
        let unit = _appointment.unit
        const needsUnit = !!unit?.id && unit.enabizEnabled === undefined
        // Hepsi yalnız randevunun birim/firma kimliğine bağlı: sırayla değil aynı anda.
        await Promise.all([
            ApiService.Doctor.GetDoctorList({ unitId: _appointment?.unit?.id }).then(res => {
                _doctors = res.data
            }),
            // Hafif liste: seçim için gereken alanlar (not/iletişim taşımaz).
            ApiService.Institution.GetInstitutionSelectListByFirm(_appointment?.firm?.id).then(res => {
                _institutions = res.data
            }),
            // Hafif liste: seçim için gereken alanlar (her öğede birim nesnesi taşımaz).
            ApiService.Operation.GetOperationSelectList(_appointment?.unit?.id).then(res => {
                _operations = (res.data || []).map(x => ({
                    ...x,
                    subItems: JSON.parse(x.subItems ?? '[]')
                }))
            }),
            needsUnit && ApiService.Unit.GetUnit(unit.id).then(unitRes => {
                if (unitRes.success) {
                    unit = { ...unit, enabizEnabled: unitRes.data?.enabizEnabled === true }
                }
            }),
        ])

        const doctor = _doctors?.length > 0 && !_appointment?.doctor?.id ? _doctors[0] : _appointment.doctor
        const institution = _institutions?.length > 0 && !_appointment.institution.id ? (_institutions.find(x => x.isDefault) || _institutions[0]) : _appointment.institution
        const appointmentTypeId = _appointment.appointmentTypeId || institution?.appointmentType?.id

        await Promise.all([
            loadInstitutionPriceList(institution.id, _operations),
            loadAppointmentTypePriceList(appointmentTypeId, _operations),
        ])

        setDoctors(_doctors)
        setInstitutions(_institutions)
        setOperationList(_operations)
        // Yüklenen işlem kümesini baseline olarak işaretle → effect yüklemede recompute yapmaz.
        opsSigRef.current = opsSig(_appointment.appointmentOperations)
        setAppointment({
            ..._appointment,
            doctor,
            institution,
            unit,
            appointmentTypeId
        })
        setCurrency(_appointment.currency || 'TRY')

        // Yükleme sürerken kart kapanmış olabilir (seçim değişti / iptal).
        if (!formRef.current)
            return

        formRef.current.setFieldsValue({
            doctorId: doctor?.id,
            institutionId: institution?.id,
            checkIn: false,
            anamnez: _appointment.anamnez,
            patientComplaintHistory: _appointment.patientComplaintHistory,
            provisionNo: _appointment.provisionNo,
            note: _appointment.note,
            privateNote: _appointment.privateNote,
            startDate: _appointment.startDate,
            endDate: _appointment.endDate,
            appointmentTypeId: appointmentTypeId
        })
    }

    const calcEndDate = (operations, startDate) => {
        if (operations && operations.length > 0) {
            let sum = 0;
            operations.forEach(op => sum += op.processMinute)
            return moment(startDate).add(sum, 'minutes');
        }
        else {
            return moment(startDate).add(15, 'minutes');
        }
    }

    // Randevu başka bir güne/saate taşınınca bitiş de aynı süre farkıyla kaydırılır; kayıtlı
    // süre (bitiş - başlangıç) korunur. Süre okunamıyorsa işlemlerden yeniden hesaplanır.
    const shiftEndDate = (newStart, prevStart, prevEnd) => {
        if (!newStart) return prevEnd;
        const duration = prevStart && prevEnd ? moment(prevEnd).diff(moment(prevStart), 'minutes') : 0;
        if (duration <= 0) return calcEndDate(appointment.appointmentOperations, newStart);
        return moment(newStart).add(duration, 'minutes');
    }

    // Takvimden seçilen aralığı forma yazar. KAYDETMEZ — normal akıştaki gibi kullanıcı
    // "Kaydet" diyene kadar sadece form/state güncellenir (tarih alanına elle yazmakla aynı).
    // setFieldsValue onValuesChange'i tetiklemediği için state elle senkronlanıyor.
    const applyPickedSlot = ({ start, end }) => {
        if (!start) return;

        const startDate = moment(start);
        const pickedMinutes = end ? moment(end).diff(startDate, 'minutes') : 0;
        const currentMinutes = appointment.startDate && appointment.endDate
            ? moment(appointment.endDate).diff(moment(appointment.startDate), 'minutes')
            : 0;

        // Tek slot'a tıklandıysa (= bir adım) randevunun mevcut süresi korunur; kullanıcı
        // gerçekten bir aralık sürüklediyse seçtiği aralık aynen uygulanır.
        const minutes = pickedMinutes > CALENDAR_SLOT_MINUTES
            ? pickedMinutes
            : (currentMinutes > 0 ? currentMinutes : pickedMinutes);

        const endDate = moment(startDate).add(minutes, 'minutes');

        formRef.current?.setFieldsValue({ startDate, endDate });
        setAppointment(a => ({ ...a, startDate, endDate }));
        setSlotPickerOpen(false);
    }

    // İşlem kümesi imzası: operationId + süre. Fiyat/iskonto gibi değişiklikler imzayı
    // değiştirmez → bitiş yeniden hesaplanmaz. Yalnızca işlem eklenince/çıkınca/süre değişince değişir.
    const opsSig = (operations) => (operations || []).map(o => `${o.operationId}:${o.processMinute}`).sort().join('|');

    // Bitiş tarihi SADECE işlemler değiştiğinde yeniden hesaplanır. Anamnez/not düzenleyip
    // kaydetmek buraya girmez (appointmentOperations referansı değişmez). Böylece kayıtlı bitiş
    // korunur; işlem ekle/çıkar/süre değiştir → bitiş otomatik güncellenir (silme dahil tek yer).
    useEffect(() => {
        const sig = opsSig(appointment.appointmentOperations);
        if (opsSigRef.current === null) { opsSigRef.current = sig; return; }
        if (sig === opsSigRef.current) return;
        opsSigRef.current = sig;
        const start = formRef.current?.getFieldsValue().startDate || appointment.startDate;
        const end = calcEndDate(appointment.appointmentOperations, start);
        setAppointment(a => ({ ...a, endDate: end }));
        if (formRef.current) formRef.current.setFieldsValue({ endDate: end });
    }, [appointment.appointmentOperations]);

    const onSelectOperation = async (id) => {
        const index = appointment.appointmentOperations.findIndex(x => x.operationId === id);
        if (index > -1)
            return;

        const operation = operationList.find(x => x.id === id);
        const institutionPrice = priceList.current.find(x => x.operationId === id)?.price
        const appTypePrice = appointmentTypePriceList.current.find(x => x.operationId === id)?.price
        const appType = appointmentTypes.current.find(x => x.id === appointment.appointmentTypeId)

        let price = operation?.price
        if (appType?.overrideInstitutionPrice) {
            price = appTypePrice
        }
        else if (institutionPrice) {
            price = institutionPrice
        }

        const ops = [
            ...appointment.appointmentOperations,
            {
                ...operation,
                id: v4(),
                operationId: operation.id,
                priceModified: false,
                price: price,
                institutionPrice: 300,
                discount: 0,
                subItems: [],
                operation
            }
        ]

        setAppointment({
            ...appointment,
            appointmentOperations: ops
        })

        formRef.current.setFieldsValue({
            endDate: calcEndDate(ops, formRef.current.getFieldsValue().startDate)
        })
    }

    const getMaxDiscount = () => {
        const institution = institutions.find(x => x.id === appointment?.institution?.id);
        return Math.min(institution?.discount, user?.discount)
    }

    const updateOperationData = (id, key, value) => {
        const ops = [...appointment.appointmentOperations].map(x => {
            delete x.error;
            return x;
        });

        const index = ops.findIndex(x => x.id === id);
        if (index > -1) {
            const institutionPrice = priceList.current.find(x => x.institutionId === appointment.institution.id && x.operationId === ops[index]?.operationId)?.price
            if (key === 'price') {
                if (institutionPrice > value) {
                    ops[index]['discount'] = 100 - (value * 100 / institutionPrice)
                }
                else {
                    ops[index]['discount'] = 0
                }
            }
            else if (key === 'discount') {
                if (value > 0) {
                    ops[index]['price'] = institutionPrice - (institutionPrice * value / 100)
                }
                else {
                    ops[index]['price'] = institutionPrice;
                }
            }

            ops[index][key] = value;
        }

        setAppointment({
            ...appointment,
            appointmentOperations: ops
        });
    }

    const updateCurrency = (value) => {
        setCurrency(value)
        setAppointment({
            ...appointment,
            currency: value,
        });
    }

    const priceListKey = (id, operations) => `${id}|${(operations || []).map(x => x.id).sort((a, b) => a - b).join(',')}`

    const loadInstitutionPriceList = async (institutionId, operations) => {
        const key = priceListKey(institutionId, operations)
        if (institutionIdRef.current === key)
            return
        institutionIdRef.current = key

        const res = await ApiService.Operation.GetInstitutionPriceList(institutionId, operations.map(x => x.id));
        // Bu sırada başka randevuya/kuruma geçildiyse eski yanıt yenisinin üstüne yazmasın.
        if (institutionIdRef.current !== key)
            return
        if (res.success) {
            priceList.current = res.data
            setPriceListVersion(v => v + 1)
        }
        else {
            institutionIdRef.current = null
        }
    }

    const loadAppointmentTypePriceList = async (typeId, operations) => {
        const key = priceListKey(typeId, operations)
        if (appointmentTypeIdRef.current === key)
            return
        appointmentTypeIdRef.current = key

        const res = await ApiService.Operation.GetAppointmentTypePriceList(typeId, operations.map(x => x.id));
        if (appointmentTypeIdRef.current !== key)
            return
        if (res.success) {
            appointmentTypePriceList.current = res.data
            setPriceListVersion(v => v + 1)
        }
        else {
            appointmentTypeIdRef.current = null
        }
    }

    const deleteOtherOperation = (row) => {
        setAppointment({
            ...appointment,
            appointmentOtherOperations: appointment.appointmentOtherOperations.filter(x => x.id !== row.id),
        })
    }

    const onOtherOperationFinish = (formData) => {
        setAppointment({
            ...appointment,
            appointmentOtherOperations: [
                ...appointment.appointmentOtherOperations,
                {
                    id: v4(),
                    ...formData,
                }
            ],
        })
        formOtherOperationRef.current.resetFields()
    }

    const onStateChange = async (stateId, stateDescForm, state, consumableConfirmations) => {
        formRef.current.setFieldsValue({
            appointmentStateId: stateId
        })

        setAppointment({
            ...appointment,
            state: {
                id: stateId,
                stateGroup: state?.stateGroup
            },
            stateDesc: stateDescForm || {}
        })

        consumables.setConfirmations(Array.isArray(consumableConfirmations) ? consumableConfirmations : null)
    }

    const onFiles = () => {
        const component = (
            <AppointmentFiles appointmentId={appointmentId} />
        )
        openModal('', component, () => {
            onStateChanged && onStateChanged('files')
        })
    }

    // Kurum/tür fiyat tanımı olmayan işlemde fiyat undefined: NaN yerine tire.
    const formatOptionalPrice = (price) => price === undefined || price === null ? '—' : Helper.Currency.format(price)

    const getInstitutionPrice = (operationId) => {
        return priceList.current.find(x => x.institutionId === appointment.institution.id && x.operationId === operationId)?.price
    }

    const getAppointmentTypePrice = (operationId) => {
        return appointmentTypePriceList.current.find(x => x.appointmentTypeId === appointment.appointmentTypeId && x.operationId === operationId)?.price
    }

    const getInstitutionTotalPrice = () => {
        return priceList.current?.filter(x => appointment.appointmentOperations.map(y => y.operationId).includes(x.operationId))?.reduce((prev, val) => prev += val.price, 0)
    }

    const getAppointmentTypeTotalPrice = () => {
        return appointmentTypePriceList.current?.filter(x => appointment.appointmentOperations.map(y => y.operationId).includes(x.operationId))?.reduce((prev, val) => prev += val.price, 0)
    }

    const getTotalPrice = () => {
        return appointment?.appointmentOperations?.reduce((prev, val) => prev += val.price, 0)
    }

    const operationsPrice = appointment.appointmentOperations?.reduce((prev, val) => prev += val?.operation?.price, 0) || 0;
    const extraOperationsPrice = appointment.appointmentOtherOperations?.reduce((prev, val) => prev += val.price, 0) || 0;
    const institutionPrice = getInstitutionTotalPrice();
    const specialPrice = getAppointmentTypeTotalPrice();
    const grandTotal = getTotalPrice();
    const activeCurrency = currency || 'TRY';

    const renderSummaryContent = () => {
        return (
            // Mobilde sola yaslı (sağa yaslanınca soldan anlamsız boşluk kalıyordu), masaüstünde sağda
            <div style={{ display: 'flex', justifyContent: screens.md ? 'end' : 'flex-start', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Text strong style={{ fontSize: 16 }}>{t('Total')}:</Text>
                        <Text strong style={{ color: '#1890ff', fontSize: 18 }}>{Helper.Currency.format(grandTotal, { currency: activeCurrency })}</Text>
                    </div>
                    <Space split={<Divider type="vertical" />} style={{ flexWrap: 'wrap' }}>
                        <Text>{t('List')}: <Text strong>{Helper.Currency.format(operationsPrice)}</Text></Text>
                        {extraOperationsPrice > 0 && (
                            <Text>{t('Extra')}: <Text strong>{Helper.Currency.format(extraOperationsPrice, { currency: activeCurrency })}</Text></Text>
                        )}
                        {(institutionPrice > 0 || specialPrice > 0) && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {t('Institution')}: {Helper.Currency.format(institutionPrice)} / {t('Special')}: {Helper.Currency.format(specialPrice)}
                            </Text>
                        )}
                    </Space>
                </div>
            </div>
        )
    }

    const getPaymentSummary = (colSpan) => {
        return (
            <Table.Summary fixed>
                <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                    <Table.Summary.Cell index={0} colSpan={colSpan}>
                        {renderSummaryContent()}
                    </Table.Summary.Cell>
                </Table.Summary.Row>
            </Table.Summary>
        )
    }



    useEffect(() => {
        loadAppointment(appointmentId)
    }, [appointmentId]);

    useEffect(() => {
        if (appointment?.institution?.id) {
            loadInstitutionPriceList(appointment.institution.id, operationList)
            setAppointment({ ...appointment })
        }
    }, [appointment?.institution?.id]);

    // Hafif kurum listesi notu taşımaz: listeden seçilen kurumun notunu (kartın altında gösterilir)
    // yalnız o kurum için getir. Kayıtlı randevuda GetAppointment notu zaten getirir → istek atılmaz.
    useEffect(() => {
        const id = appointment?.institution?.id
        if (!id || appointment.institution.note !== undefined)
            return
        let cancelled = false
        ApiService.Institution.GetInstitution(id).then(res => {
            if (cancelled || !res.success)
                return
            setAppointment(prev => prev?.institution?.id === id
                ? { ...prev, institution: { ...prev.institution, note: res.data?.note ?? null } }
                : prev)
        })
        return () => { cancelled = true }
    }, [appointment?.institution?.id, appointment?.institution?.note]);

    useEffect(() => {
        if (appointment?.appointmentTypeId) {
            loadAppointmentTypePriceList(appointment?.appointmentTypeId, operationList)
            setAppointment({ ...appointment })
        }
    }, [appointment?.appointmentTypeId]);

    useEffect(() => {
        onAppointmentChange && onAppointmentChange(appointment)
    }, [appointment]);

    const handleSave = async () => {
        // Tamamlama onayı durum seçilirken alınır; sonradan işlem/sarf değiştiyse (onay düştüyse) burada yeniden sorulur.
        let consumableConfirmations = null
        if (isCompletingState(appointment.state?.stateGroup?.id)) {
            consumableConfirmations = consumables.confirmations
            if (!consumableConfirmations && needsConsumableConfirm(consumables.pendingLines)) {
                consumableConfirmations = await askConsumableConfirm(consumables.pendingLines)
                if (!consumableConfirmations)
                    return
                consumables.setConfirmations(consumableConfirmations)
            }
        }

        const res = await onSave({
            ...appointment,
            // Bitiş, state'teki güncel değer olarak gider (işlem effect'i ile senkron).
            // Yeniden hesaplanmaz → sadece anamnez düzenleyip kaydetmek bitişi değiştirmez.
            endDate: appointment.endDate,
            consumables: consumables.buildPayload(),
            consumableConfirmations
        })

        // Randevu başka sekmede/kullanıcıda değişmiş: ekrandaki (kaydedilmemiş) değişiklikler
        // kullanıcı istemedikçe silinmez; güncel hali yüklemek onun kararı.
        if (res?.errorData?.code === 'APPOINTMENT_STALE') {
            Modal.confirm({
                title: t('Appointment is out of date'),
                content: t('Your changes were not saved. Loading the current version discards the unsaved changes on this screen.'),
                okText: t('Load current version'),
                cancelText: t('Close'),
                onOk: () => loadAppointment(appointmentId)
            });
            return
        }

        // Kayıt başarısızsa (KVKK onayı, hasta kaydı, doğrulama vb.) yeniden yükleme girilenleri silerdi
        if (res?.success && !appointment.newRecord) {
            await loadAppointment(appointmentId)
            consumables.reload()
        }
    }

    // --- FİYAT TEKLİFİ AKTARIMI ---
    const transferQuote = (mode) => {
        const currentUnitId = appointment?.unit?.id;
        const quote = PriceQuoteStorage.getForUnit(currentUnitId);
        if (!quote) {
            setHasQuote(false);
            return;
        }

        const quoteOps = (quote.operationList || []).map(op => ({
            ...op,
            id: v4(), // yeni id — appointment ops'ta çakışmasın
        }));
        const quoteOtherOps = (quote.otherOperations || []).map(op => ({
            ...op,
            id: v4(),
        }));

        const newOps = mode === 'replace'
            ? quoteOps
            : [...appointment.appointmentOperations, ...quoteOps];

        const newOtherOps = mode === 'replace'
            ? quoteOtherOps
            : [...appointment.appointmentOtherOperations, ...quoteOtherOps];

        // Kurum + randevu türünü set et (yalnızca quote bu birimle eşleşiyorsa)
        let nextInstitution = appointment.institution;
        let nextAppointmentTypeId = appointment.appointmentTypeId;
        if (quote.unitMatches) {
            if (quote.institutionId) {
                const inst = institutions.find(x => x.id === quote.institutionId);
                // Aynı kurumsa mevcut nesne kalır (hafif liste öğesi notu taşımaz).
                if (inst && inst.id !== appointment.institution?.id) {
                    nextInstitution = inst;
                }
            }
            if (quote.appointmentTypeId) {
                nextAppointmentTypeId = quote.appointmentTypeId;
            }
        }

        const newAppointment = {
            ...appointment,
            institution: nextInstitution,
            appointmentTypeId: nextAppointmentTypeId,
            appointmentOperations: newOps,
            appointmentOtherOperations: newOtherOps,
            currency: quote.currency || appointment.currency,
        };

        setAppointment(newAppointment);
        setCurrency(quote.currency || currency);

        // Form alanlarını da güncelle (institution, appointmentType, endDate)
        if (formRef.current) {
            formRef.current.setFieldsValue({
                institutionId: nextInstitution?.id,
                appointmentTypeId: nextAppointmentTypeId,
                endDate: calcEndDate(newOps, formRef.current.getFieldsValue().startDate),
            });
        }

        // Fiyat listelerini de yeni seçimlere göre tetikle
        if (quote.unitMatches) {
            if (nextInstitution?.id) {
                loadInstitutionPriceList(nextInstitution.id, operationList);
            }
            if (nextAppointmentTypeId) {
                loadAppointmentTypePriceList(nextAppointmentTypeId, operationList);
            }
        }

        // Sadece aktarılan işlemleri hafızadan çıkar (metadata + diğer birimlerin ops'u kalır)
        PriceQuoteStorage.removeTransferredForUnit(currentUnitId);
        setHasQuote(false);
        message.success(mode === 'replace' ? t('Existing operations replaced') : t('Lists merged'));
    };

    const discardQuote = () => {
        Modal.confirm({
            title: 'Fiyat teklifini sil',
            content: t('Delete the saved price quote?'),
            okText: t('Yes'),
            cancelText: t('No'),
            okButtonProps: { danger: true },
            onOk: () => {
                PriceQuoteStorage.clear();
                setHasQuote(false);
                message.info(t('Price quote removed from memory'));
            }
        });
    };

    const canShowSave = appointmentId && hasPermission(PERMISSIONS.APPOINTMENT_CREATE_EDIT)
    const canShowPayments = !(typeof appointmentId === 'object') && hasPermission(PERMISSIONS.PAYMENT_CREATE_EDIT)
    const canShowFiles = !(typeof appointmentId === 'object') && hasPermission(PERMISSIONS.APPOINTMENT_CREATE_EDIT)
    const canShowFormDownload = !(typeof appointmentId === 'object')
    const canShowDelete = !(typeof appointmentId === 'object') && hasPermission(PERMISSIONS.APPOINTMENT_DELETE)
    const canShowClose = !!appointmentId

    // Randevuya kayıtlı işlemleri düzenleme/silme yetkisi. Yetkisi olmayan kullanıcı yalnızca yeni işlem ekleyebilir.
    const canEditOperation = hasPermission(PERMISSIONS.APPOINTMENT_EDIT_OPERATION)
    const isOperationLocked = (op) => op?.persisted === true && !canEditOperation

    const renderActionButtons = () => {
        if (!screens.md) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
                    {/* Satır 1: Kaydet (geniş) + Sil + Kapat */}
                    {(canShowSave || canShowDelete || canShowClose) && (
                        <div style={{ display: 'flex', gap: 8 }}>
                            {canShowSave && (
                                <Popover
                                    content={(
                                        <Typography.Text>
                                            {t('Changes to the patient card will be saved.')}
                                        </Typography.Text>
                                    )}
                                >
                                    <Button
                                        type={'primary'}
                                        size={'middle'}
                                        style={{ flex: 2, minWidth: 0 }}
                                        onClick={handleSave}
                                    >
                                        {t('Save')}
                                    </Button>
                                </Popover>
                            )}
                            {canShowDelete && (
                                <Button
                                    type={'primary'}
                                    size={'middle'}
                                    danger
                                    style={{ flex: 1, minWidth: 0 }}
                                    onClick={async () => { onDelete(appointment) }}
                                >
                                    {t('Delete')}
                                </Button>
                            )}
                            {/* Kapat: çerçeveli kırmızı düğme (antd'de "warning" tipi yok, metin linki gibi görünüyordu) */}
                            {canShowClose && (
                                <Button
                                    size={'middle'}
                                    danger
                                    style={{ flex: 1, minWidth: 0 }}
                                    onClick={async () => { onCancel() }}
                                >
                                    {t('Close')}
                                </Button>
                            )}
                        </div>
                    )}

                    {/* Ödemeler / Dosyalar / Onam Formu mobilde gösterilmez */}
                </div>
            )
        }

        return (
            <>
                <Button.Group>
                    {
                        canShowSave && (
                            <Popover
                                content={(
                                    <Typography.Text>
                                        {t('Changes to the patient card will be saved.')}
                                    </Typography.Text>
                                )}
                            >
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <Button
                                        type={'primary'}
                                        size={'small'}
                                        onClick={handleSave}
                                    >
                                        {t('Save')}
                                    </Button>
                                    {/* {
                                        appointment?.state?.stateGroup?.id === 2 && hasPermission(PERMISSIONS.ENABIZ) && (
                                            <Button
                                                style={{ marginLeft: 4 }}
                                                size={'small'}
                                                type={'primary'}
                                                onClick={async () => {
                                                    const res = await ApiService.AppointmentTeletip.SendEnabizBatch(appointmentId)
                                                    if (res.success) {
                                                        await loadAppointment(appointmentId)
                                                    }
                                                }}
                                            >
                                                {t('Send Enabiz')}
                                            </Button>
                                        )
                                    } */}
                                    <Borcodeprinter
                                        appointmentId={appointmentId} />
                                </div>
                            </Popover>
                        )
                    }
                </Button.Group>
                <div style={{ flex: 1 }} />
                <Button.Group style={{ flexWrap: 'wrap' }}>
                    {
                        canShowPayments && (
                            <Button size={'small'} onClick={onPayment} className={'bg-success text-white'}
                                style={{ width: 75 }}>{t('Payments')}</Button>
                        )
                    }
                    {
                        canShowFiles && (
                            <Button size={'small'} onClick={onFiles} className={'bg-success text-white'}
                                style={{ width: 75 }}>{t('Files')}</Button>
                        )
                    }
                    {
                        canShowFormDownload && (
                            <FormDownload
                                appointmentId={appointmentId}
                                unitId={appointment?.unit?.id}
                            />
                        )
                    }
                    {
                        canShowDelete && (
                            <Button
                                style={{ width: 60 }}
                                type={'primary'}
                                size={'small'}
                                danger={true}
                                onClick={async () => {
                                    onDelete(appointment)
                                }}
                            >
                                {t('Delete')}
                            </Button>
                        )
                    }
                    {
                        canShowClose && (
                            <Button
                                style={{ width: 60 }}
                                type={'warning'}
                                size={'small'}
                                danger={true}
                                onClick={async () => {
                                    onCancel()
                                }}
                            >
                                {t('Close')}
                            </Button>
                        )
                    }
                </Button.Group>
            </>
        )
    }

    // Kart (mobil) ve kompakt satır (masaüstü) aynı işlem davranışlarını kullanır.
    const isOperationPriceDisabled = (op) =>
        (Helper.Date.isBeforeToday(appointment.startDate) && !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)) ||
        isOperationLocked(op)

    const openSubItems = (op, ops) => {
        openModal(
            t(''),
            <AppointmentOperationSubItemsEdit
                currency={currency}
                subItems={ops.subItems}
                selectedSubItems={op.subItems ?? []}
                onSave={(items) => {
                    const updatedOps = appointment.appointmentOperations.map(x => {
                        if (x.id === op.id) {
                            return {
                                ...x,
                                price: items.reduce((prev, val) => prev += val.price, 0),
                                subItems: items
                            }
                        }
                        return x
                    })
                    setAppointment({
                        ...appointment,
                        appointmentOperations: updatedOps
                    })
                    closeModal()
                }}
            />,
            () => { }
        )
    }

    const onOperationPriceChange = (op, ops, newPrice) => {
        if (ops?.subItems?.length > 0) {
            const updatedOperations = appointment.appointmentOperations.map(mapOp => {
                if (mapOp.id === op.id) {
                    const subItemsPrice = mapOp.subItems.reduce((sum, item) => sum + item.price, 0)
                    const ratio = newPrice / subItemsPrice

                    mapOp.price = newPrice
                    mapOp.subItems = mapOp.subItems.map(sub => ({
                        ...sub,
                        price: parseFloat((sub.price * ratio).toFixed(2))
                    }))

                    const newSubItemsPrice = mapOp.subItems.reduce((sum, item) => sum + item.price, 0)
                    const diff = newPrice - newSubItemsPrice
                    if (Math.abs(diff) > 0.01 && mapOp.subItems.length > 0) {
                        mapOp.subItems[0].price = parseFloat((mapOp.subItems[0].price + diff).toFixed(2))
                    }

                    return { ...mapOp }
                }
                return mapOp
            })

            setAppointment({
                ...appointment,
                appointmentOperations: updatedOperations
            })
        } else {
            updateOperationData(op.id, 'price', newPrice)
        }
    }

    const deleteOperation = (op) => {
        setAppointment({
            ...appointment,
            appointmentOperations: appointment.appointmentOperations.filter(x => x.id !== op.id),
        })
    }

    // Masaüstü: işlem başına tek satır. Solda ad + liste/kurum/özel fiyatı, sağda erişim no, süre, fiyat, sil.
    // Para birimi tüm işlemler için tek; her satırda tekrar etmesin diye başlıkta.
    // Ekstra işlem ekleme formu — masaüstünde sekme çubuğunun sağında, mobilde sekmenin içinde
    const extraOperationForm = (
        <Form
            size={'small'}
            ref={formOtherOperationRef}
            layout={!screens.md ? 'vertical' : 'inline'}
            onFinish={onOtherOperationFinish}
        >
            <Form.Item
                name={'name'}
                rules={[{ required: true, message: '' }]}
                style={!screens.md ? { marginBottom: 8 } : { marginInlineEnd: 6 }}
            >
                <Input placeholder={t('Name')} style={!screens.md ? undefined : { width: 200 }} />
            </Form.Item>
            <Form.Item
                name={'price'}
                rules={[{ required: true, message: '' }]}
                style={!screens.md ? { marginBottom: 8 } : { marginInlineEnd: 6 }}
            >
                <InputNumber
                    style={!screens.md ? { width: '100%' } : { width: 140 }}
                    placeholder={t('Price')}
                    min={0}
                    step={0.01}
                    prefix={Currency[currency]}
                />
            </Form.Item>
            <Form.Item style={!screens.md ? { marginBottom: 8 } : { marginInlineEnd: 0 }}>
                <Button
                    className={'bg-warning'}
                    htmlType={'submit'}
                    style={!screens.md ? { width: '100%' } : {}}
                >
                    +
                </Button>
            </Form.Item>
        </Form>
    );

    const renderCompactOperations = () => {
        // Soluk fiyat satırları: değerler italik, ayraç nokta metinden bir tık iri.
        const metaValue = (text) => <span style={{ fontStyle: 'italic' }}>{text}</span>
        const metaDot = <span style={{ fontSize: '1.5em', lineHeight: 0, margin: '0 4px', verticalAlign: '-0.1em' }}>·</span>
        const grid = { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 120px 64px 136px 24px', gap: 6, alignItems: 'center' }
        return (
            <div style={{ marginTop: 8, border: '1px solid #f0f0f0', borderRadius: 6, background: '#fff' }}>
                <div style={{ ...grid, padding: '4px 8px', background: '#fafafa', borderBottom: '1px solid #f0f0f0', fontSize: 11, color: '#8c8c8c' }}>
                    <span>{t('Name')}</span>
                    <span>{t('Accession Number')}</span>
                    <span>{t('Min')}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {t('Price')}
                        <TitleCurrencySelector
                            value={currency}
                            disabled={Helper.Date.isBeforeToday(appointment.startDate) && !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)}
                            onChange={(e) => updateCurrency(e)}
                            compact
                        />
                    </span>
                    <span />
                </div>
                {appointment.appointmentOperations.length === 0 && (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No Operations')} style={{ margin: '8px 0' }} />
                )}
                {appointment.appointmentOperations.map(op => {
                    const ops = operationList.find(x => x.id === op.operationId)
                    const locked = isOperationLocked(op)
                    return (
                        <div key={op.id} style={{ ...grid, padding: '4px 8px', borderBottom: '1px solid #f5f5f5' }}>
                            <div style={{ minWidth: 0, lineHeight: 1.3 }}>
                                {/* İpucu yalnız ad sığmayıp kesildiğinde çıkar. */}
                                <Text
                                    ellipsis={{ tooltip: op?.operation?.name }}
                                    style={{ display: 'block', fontSize: 12.5, fontWeight: 500, color: '#262626' }}
                                >
                                    {op?.operation?.name}
                                </Text>
                                <div style={{ fontSize: 10.5, color: '#808080', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {t('List')}: {metaValue(Helper.Currency.format(op?.operation?.price))}
                                    {metaDot}
                                    {t('Institution')}: {metaValue(formatOptionalPrice(getInstitutionPrice(op?.operationId)))}
                                    {metaDot}
                                    {t('Special')}: {metaValue(formatOptionalPrice(getAppointmentTypePrice(op?.operationId)))}
                                </div>
                            </div>
                            <Input
                                size={'small'}
                                disabled={locked}
                                placeholder={t('Accession Number')}
                                value={op.accessionNumber}
                                onChange={(e) => updateOperationData(op.id, 'accessionNumber', e.target.value)}
                            />
                            <InputNumber
                                size={'small'}
                                style={{ width: '100%' }}
                                disabled={locked}
                                value={op?.processMinute}
                                onChange={(val) => updateOperationData(op.id, 'processMinute', val)}
                            />
                            <div style={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                                <InputNumber
                                    size={'small'}
                                    style={{ flex: 1, minWidth: 0 }}
                                    disabled={isOperationPriceDisabled(op)}
                                    prefix={Currency[appointment.currency]}
                                    value={op?.price}
                                    step={0.01}
                                    onChange={(newPrice) => onOperationPriceChange(op, ops, newPrice)}
                                />
                                {ops && ops.subItems?.length > 0 && (
                                    <Tooltip title={t('Sub Operations')}>
                                        <Button
                                            size={'small'}
                                            icon={<SmallDashOutlined />}
                                            disabled={locked}
                                            onClick={() => openSubItems(op, ops)}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                            <Tooltip title={t('Delete')}>
                                <Button
                                    size={'small'}
                                    icon={<DeleteOutlined style={isOperationPriceDisabled(op) ? undefined : { color: '#ff4d4f' }} />}
                                    disabled={isOperationPriceDisabled(op)}
                                    onClick={() => deleteOperation(op)}
                                />
                            </Tooltip>
                        </div>
                    )
                })}
                {/* Toplam fiyat sütununun hizasında; döküm ilk sütunun altında tek satır, soluk.
                    Tek işlem + ekstra yokken döküm satırdakinin aynısı → tekrar etmesin, yalnız toplam kalsın. */}
                <div style={{ ...grid, padding: '6px 8px', background: '#fafafa', borderTop: '1px solid #e8e8e8' }}>
                    <div style={{ gridColumn: '1 / 4', alignSelf: 'end', fontSize: 11, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
                    {(appointment.appointmentOperations.length > 1 || extraOperationsPrice > 0) && <>
                        {t('List')}: {metaValue(Helper.Currency.format(operationsPrice))}
                        {extraOperationsPrice > 0 && <>{metaDot}{t('Extra')}: {metaValue(Helper.Currency.format(extraOperationsPrice, { currency: activeCurrency }))}</>}
                        {(institutionPrice > 0 || specialPrice > 0) && (
                            <>
                                {metaDot}{t('Institution')}: {metaValue(Helper.Currency.format(institutionPrice))}
                                {metaDot}{t('Special')}: {metaValue(Helper.Currency.format(specialPrice))}
                            </>
                        )}
                    {/* Döküm fiyatlarının anlamı: metnin akışında, son değerin hemen ardında */}
                    <Tooltip
                        overlayStyle={{ maxWidth: 360 }}
                        title={
                            <div style={{ fontSize: 12 }}>
                                <div><b>{t('List')}:</b> {t('General list price of the operation.')}</div>
                                <div><b>{t('Institution')}:</b> {t('Contracted price of the selected institution.')}</div>
                                <div><b>{t('Special')}:</b> {t('Special price defined for the appointment type.')}</div>
                                <div><b>{t('Extra')}:</b> {t('Total of the extra items.')}</div>
                            </div>
                        }
                    >
                        <InfoCircleOutlined style={{ marginLeft: 8, cursor: 'help' }} />
                    </Tooltip>
                    </>}
                    </div>
                    <div style={{ paddingLeft: 8, lineHeight: 1.2 }}>
                        <Text strong style={{ color: '#1890ff', fontSize: 16, whiteSpace: 'nowrap' }}>
                            {Helper.Currency.format(grandTotal, { currency: activeCurrency })}
                        </Text>
                    </div>
                    <span />
                </div>
            </div>
        )
    }

    const renderMobileOperations = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                {appointment.appointmentOperations.length === 0 && (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No Operations')} />
                )}
                {appointment.appointmentOperations.map(op => {
                    const ops = operationList.find(x => x.id === op.operationId)
                    return (
                        <Card size="small"
                            type="inner"
                            key={op.id}
                            headStyle={{ background: '#f9f0ff' }}
                            title={<span style={{ fontSize: 14, fontWeight: 500, color: '#262626' }}>{op?.operation?.name}</span>}
                            extra={
                                <Button
                                    size={'middle'}
                                    danger
                                    disabled={
                                        (Helper.Date.isBeforeToday(appointment.startDate) &&
                                        !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)) ||
                                        isOperationLocked(op)
                                    }
                                    onClick={() => {
                                        setAppointment({
                                            ...appointment,
                                            appointmentOperations: appointment.appointmentOperations.filter(x => x.id !== op.id),
                                        })
                                    }}>
                                    Sil
                                </Button>
                            }
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div>
                                    <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                        {t('Accession')}
                                    </Typography.Text>
                                    <Input
                                        style={{ width: '100%' }}
                                        size={'middle'}
                                        disabled={isOperationLocked(op)}
                                        placeholder={t('Accession')}
                                        value={op.accessionNumber}
                                        onChange={(e) => {
                                            updateOperationData(op.id, 'accessionNumber', e.target.value)
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: 8 }}>
                                    <div style={{ flex: 1 }}>
                                        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                            {t('Min')}
                                        </Typography.Text>
                                        <InputNumber
                                            style={{ width: '100%' }}
                                            size={'middle'}
                                            disabled={isOperationLocked(op)}
                                            placeholder={t('Min')}
                                            defaultValue={op?.processMinute}
                                            value={op?.processMinute}
                                            onChange={(val) => {
                                                updateOperationData(op.id, 'processMinute', val)
                                            }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                                            {t('Currency')}
                                        </Typography.Text>
                                        <TitleCurrencySelector
                                            value={currency}
                                            disabled={Helper.Date.isBeforeToday(appointment.startDate) && !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)}
                                            onChange={(e) => {
                                                updateCurrency(e)
                                            }}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>{t('Price')}</Typography.Text>
                                        {ops && ops.subItems?.length > 0 && (
                                            <Button
                                                icon={<SmallDashOutlined />}
                                                size={'small'}
                                                disabled={isOperationLocked(op)}
                                                onClick={() => openSubItems(op, ops)}
                                            >
                                                Alt İşlemler
                                            </Button>
                                        )}
                                    </div>
                                    <InputNumber
                                        disabled={isOperationPriceDisabled(op)}
                                        style={{ width: '100%' }}
                                        size={'middle'}
                                        prefix={Currency[appointment.currency]}
                                        value={op?.price}
                                        step={0.01}
                                        onChange={(newPrice) => onOperationPriceChange(op, ops, newPrice)}
                                    />
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#999', borderTop: '1px dashed #eee', paddingTop: 6 }}>
                                    <span>Liste: {Helper.Currency.format(op?.operation?.price)}</span>
                                    <span>{formatOptionalPrice(getInstitutionPrice(op?.operationId))} / {formatOptionalPrice(getAppointmentTypePrice(op?.operationId))}</span>
                                </div>
                            </div>
                        </Card>
                    )
                })}
                <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                    {renderSummaryContent()}
                </Card>
            </div>
        )
    }

    const renderMobileExtraOperations = () => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {appointment.appointmentOtherOperations.length === 0 && (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No Extra Operations')} />
                )}
                {appointment.appointmentOtherOperations.map((row, index) => (
                    <Card key={index} size="small" type="inner" title={row.name} extra={
                        <Button size={'small'} onClick={() => deleteOtherOperation(row)} danger={true}>X</Button>
                    }>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography.Text>{t('Price')}</Typography.Text>
                            <Typography.Text strong>{Helper.Currency.format(row.price, { currency })}</Typography.Text>
                        </div>
                    </Card>
                ))}
                <Card size="small" style={{ backgroundColor: '#fafafa' }}>
                    {renderSummaryContent()}
                </Card>
            </div>
        )
    }

    // Görsel ayrıştırma için her kart farklı bir renk vurgusu (sol kenar) alıyor.
    // Mobilde özellikle alt alta dizildiklerinde bu vurgu kartları net ayırır.
    const appointmentCardAccent = '#13c2c2';   // cyan — randevu
    const operationsCardAccent = '#722ed1';    // mor — işlemler & ödemeler
    const cardCommon = {
        marginBottom: 12,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
    };

    // E-Nabız otomatik gönderimi yalnızca Birim + Kurum + Randevu Tipi üçü de aktifse mümkündür.
    // Üçünden biri pasifse "Send E-Nabız" anahtarı kapalı olmalı ve aktifleştirilememelidir.
    const selectedAppointmentType = appointmentTypeList.find(x => x.id === appointment.appointmentTypeId)
    const enabizConfigEnabled =
        appointment?.unit?.enabizEnabled === true &&
        appointment?.institution?.enabizEnabled === true &&
        selectedAppointmentType?.enabizEnabled === true

    // Saat değişikliği için asıl yol birimin takvimi: önceki/sonraki randevular görülerek
    // boş aralık seçilir. Tarih alanı yerinde duruyor — takvimin açılamadığı durumlar
    // (birim bilgisi yok) ve hızlı elle düzeltme için.
    const canPickSlot = !!appointment?.unit?.id && hasPermission(PERMISSIONS.APPOINTMENT_CREATE_EDIT)
    const startDateLabel = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {t('Start Date')}
            {canPickSlot && (
                <Tooltip title={t('Pick the time from the unit calendar')}>
                    <Button
                        size={'small'}
                        icon={<CalendarOutlined />}
                        onClick={() => setSlotPickerOpen(true)}
                    />
                </Tooltip>
            )}
        </div>
    )

    return (
        <>
            {consumableConfirmModal}
            <Card
                title={(
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <FileTextOutlined style={{ color: appointmentCardAccent }} />
                        <Typography.Text strong>
                            {t('Appointment')} {typeof appointmentId !== 'object' ? appointmentId : ''}: {appointment.newRecord ? t('New') : t('Edit')} - {appointment?.unit?.name}
                        </Typography.Text>
                        {screens.md && renderActionButtons()}
                    </div>
                )}
                className="accent-left"
                style={{
                    backgroundColor: '#f5f5f5',
                    '--accent-color': appointmentCardAccent,
                    ...cardCommon,
                }}
                // Mobilde yatay iç boşluk 24 → 12 (dar ekranda alanlar gereksiz daralıyordu)
                headStyle={{ background: '#f5f5f5', ...(!screens.md ? { padding: '0 12px' } : {}) }}
                bodyStyle={{ background: '#f5f5f5', padding: screens.md ? '12px 24px' : '12px' }}>
                {/* Kompakt form: alanlar arası ve etiket altı boşluklar daraltıldı (yerleşim aynı) */}
                <style>{`
                    .appointment-compact-form .ant-form-item { margin-bottom: 8px; }
                    .appointment-compact-form .ant-form-item-label { padding-bottom: 2px !important; }
                    .appointment-compact-form .ant-form-item-label > label { font-size: 12px; color: #595959; }
                    .appointment-compact-form .uppercase-input { text-transform: uppercase; }
                `}</style>
                <Form
                    className={'appointment-compact-form'}
                    layout={'vertical'}
                    ref={formRef}
                onValuesChange={() => {
                    const form = formRef.current.getFieldsValue()

                    const ins = institutions.find(x => x.id === form.institutionId)
                    const doctor = doctors.find(x => x.id === form.doctorId)

                    let typeId = appointment.appointmentTypeId
                    if (form.institutionId !== appointment.institution.id) {
                        typeId = ins?.appointmentType?.id
                    }
                    else {
                        typeId = form.appointmentTypeId
                    }

                    // Başlangıç değişmediyse bitiş korunur — anamnez/not düzenlemesi bitişi kaydırmaz.
                    // Bitişin işlemlerden yeniden hesaplandığı tek yer yukarıdaki effect.
                    const startChanged = !moment(form.startDate).isSame(moment(appointment.startDate))
                    const endDate = startChanged
                        ? shiftEndDate(form.startDate, appointment.startDate, appointment.endDate)
                        : appointment.endDate

                    setAppointment({
                        ...appointment,
                        // Kurum değişmediyse mevcut nesne kalır: hafif liste öğesi notu taşımaz,
                        // her form değişikliğinde notun silinip yeniden istenmesini önler.
                        institution: form.institutionId === appointment.institution?.id
                            ? appointment.institution
                            : { ...ins },
                        doctor,
                        anamnez: form.anamnez,
                        patientComplaintHistory: form.patientComplaintHistory,
                        provisionNo: form.provisionNo,
                        privateNote: form.privateNote,
                        note: form.note,
                        startDate: form.startDate,
                        endDate,
                        appointmentTypeId: typeId
                    })

                    formRef.current.setFieldsValue({
                        appointmentTypeId: typeId,
                        endDate
                    })
                }}
            >
                <Row gutter={24}>
                    <Col xs={24} xl={12}>
                        {screens.md ? (
                            <Form.Item
                                label={startDateLabel}
                                name={'startDate'}
                                rules={[{ required: true }]}
                            >
                                <DatePicker
                                    size={'small'}
                                    format={'DD-MM-YYYY HH:mm'}
                                    style={{ width: '100%' }}
                                    showTime={true}
                                    locale={Helper.Date.getLocale()}
                                />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                label={startDateLabel}
                                name={'startDate'}
                                rules={[{ required: true }]}
                            >
                                <DateTimeSplitInput />
                            </Form.Item>
                        )}
                    </Col>
                    <Col xs={24} xl={12}>
                        {screens.md ? (
                            <Form.Item
                                label={t('End Date')}
                                name={'endDate'}
                                rules={[{ required: true }]}
                            >
                                <DatePicker
                                    size={'small'}
                                    format={'DD-MM-YYYY HH:mm'}
                                    style={{ width: '100%' }}
                                    showTime={true}
                                    disabled={true}
                                    locale={Helper.Date.getLocale()}
                                />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                label={t('End Date')}
                                name={'endDate'}
                            >
                                <div style={{
                                    padding: '6px 11px',
                                    background: '#f5f5f5',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: 4,
                                    color: '#595959',
                                    fontSize: 14,
                                    minHeight: 36,
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    {appointment.endDate ? moment(appointment.endDate).format('DD-MM-YYYY HH:mm') : '—'}
                                </div>
                            </Form.Item>
                        )}
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={t('Doctor')}
                            name={'doctorId'}
                            rules={[
                                { required: true }
                            ]}
                        >
                            <Select
                                size={'small'}
                            >
                                {doctors.map(x => {
                                    return (
                                        <Select.Option
                                            key={x.id}
                                            value={x.id}
                                        >
                                            {x.name}
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={6}>
                        <Form.Item
                            label={(
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {t('Institution')}
                                    {(
                                        (() => {
                                            if (Helper.Date.isBeforeToday(appointment.startDate)) {
                                                if (hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)) {
                                                    return true;
                                                } else {
                                                    return false;
                                                }
                                            } else {
                                                return true;
                                            }
                                        })()
                                    ) && (
                                            <Tooltip title={t('Institution')}>
                                                <Button
                                                    size={'small'}
                                                    type="default"
                                                    icon={<EyeOutlined />}
                                                    onClick={() => {
                                                        openModal(t(''), (
                                                            <InstitutionCreateEdit
                                                                showFirmSettings={false}
                                                                institutionId={appointment.institution.id}
                                                                editable={false}
                                                            />
                                                        ), async () => { })
                                                    }}
                                                />
                                            </Tooltip>
                                        )}
                                </div>
                            )}
                            name={'institutionId'}
                            rules={[
                                { required: true }
                            ]}
                        >
                            <Select
                                size={'small'}
                                showSearch={true}
                                disabled={
                                    Helper.Date.isBeforeToday(appointment.startDate) &&
                                    !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                }
                                open={institutionSelectOpen}
                                onDropdownVisibleChange={setInstitutionSelectOpen}
                                onInputKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        setTimeout(() => {
                                            setInstitutionSelectOpen(false);
                                            if (e.target && typeof e.target.blur === 'function') {
                                                e.target.blur();
                                            }
                                        }, 0);
                                    }
                                }}
                                filterOption={(input, option) => {
                                    return matchesSearch(option?.children, input)
                                }}
                            >
                                {institutions.map(x => {
                                    return (
                                        <Select.Option
                                            value={x.id}
                                            key={x.id}
                                        >
                                            {x.name}
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={6}>
                        <Form.Item
                            label={t('Appointment Type')}
                            name={'appointmentTypeId'}
                            rules={[
                                { required: true }
                            ]}
                        >
                            <AppointmentTypeSelect
                                onTypes={(types) => {
                                    appointmentTypes.current = types
                                    setAppointmentTypeList(types)
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={t('Provision No')}
                            name={'provisionNo'}
                        >
                            {/* Metin alanları büyük harf: yazarken CSS ile (imleç kaymasın), kayıtta değerin kendisi çevrilir. lang=tr → i/İ doğru */}
                            <Input size="small" className={'uppercase-input'} lang={'tr'} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={12}>
                        {
                            (hasPermission(PERMISSIONS.VIEW_PRICE)) && (<Form.Item
                                label={t('Private Note')}
                                name={'privateNote'}
                            >
                                <Input size="small" className={'uppercase-input'} lang={'tr'} />
                            </Form.Item>)
                        }
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={'Anamnez'}
                            name={'anamnez'}
                        >
                            <Input.TextArea
                                rows={2}
                                className={'uppercase-input'}
                                lang={'tr'}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={t('Patient Complaint History')}
                            name={'patientComplaintHistory'}
                        >
                            <Input.TextArea
                                rows={2}
                                className={'uppercase-input'}
                                lang={'tr'}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} xl={12}>
                        <Form.Item
                            label={t('Appointment Note')}
                            name={'note'}
                        >
                            <Input.TextArea rows={2} className={'uppercase-input'} lang={'tr'} />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            {/* Check-in ve Durum yan yana (sol) */}
                            <Form.Item>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 8,
                                        alignItems: 'center'
                                    }}
                                >
                                    <Typography.Text strong={true} style={{ whiteSpace: 'nowrap' }}>{t('Check-in')}</Typography.Text>
                                    <Switch checked={appointment.checkIn} onChange={(e) => {
                                        setAppointment({
                                            ...appointment,
                                            checkIn: e,
                                        })
                                        onCheckedChange && onCheckedChange(e)
                                    }} />
                                </div>
                            </Form.Item>
                            <Form.Item>
                                <StateChanger
                                    onStateChange={onStateChange}
                                    beforeStateChange={beforeStateChange}
                                    appointmentId={appointmentId}
                                    selectedStateId={appointment?.state?.id}
                                    checkIn={appointment.checkIn}
                                    onStates={(data) => {
                                        if (!data.find(x => x.id === appointment?.state?.id)) {
                                            setAppointment({
                                                ...appointment,
                                                state: {
                                                    id: data.find(x => x.stateGroup?.id === 1)?.id
                                                }
                                            })
                                        }
                                    }}
                                />
                            </Form.Item>
                            {/* Send E-Nabız ve Bildirim Gönder masaüstünde satırın en sağında; mobilde alt satıra
                                kırıldığında sola yaslı (marginLeft auto orada sağa itiyordu) */}
                            <Form.Item style={{ marginLeft: screens.md ? 'auto' : 0 }}>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 8,
                                        alignItems: 'center'
                                    }}
                                >
                                    <Tooltip title={
                                        enabizConfigEnabled
                                            ? t('When off, this appointment is excluded from automatic E-Nabız sending.')
                                            : t('E-Nabız sending is disabled because the Unit, Institution or Appointment Type does not have E-Nabız enabled.')
                                    }>
                                        <Typography.Text strong={true} style={{ whiteSpace: 'nowrap' }}>{t('Send E-Nabız')}</Typography.Text>
                                    </Tooltip>
                                    <Switch
                                        disabled={!enabizConfigEnabled}
                                        checked={enabizConfigEnabled && appointment.sendEnabiz !== false}
                                        onChange={(e) => {
                                            setAppointment({
                                                ...appointment,
                                                sendEnabiz: e,
                                            })
                                        }}
                                    />
                                </div>
                            </Form.Item>
                            <Form.Item>
                                <div
                                    style={{
                                        display: "flex",
                                        gap: 8,
                                        alignItems: 'center'
                                    }}
                                >
                                    <Tooltip title={t('When off, no notifications are produced/sent for this appointment.')}>
                                        <Typography.Text strong={true} style={{ whiteSpace: 'nowrap' }}>{t('Send Notifications')}</Typography.Text>
                                    </Tooltip>
                                    <Switch
                                        checked={appointment.enableNotification !== false}
                                        onChange={(e) => {
                                            setAppointment({
                                                ...appointment,
                                                enableNotification: e,
                                            })
                                        }}
                                    />
                                    {
                                        // Bildirim aksiyonları dropdown'ı. Seçenekler dinamik; hiç seçenek yoksa hiç gösterilmez.
                                        (() => {
                                            const items = [];
                                            // İlk seçenek: hatırlatma sistemde aktifse ve randevu kayıtlıysa elle hatırlatma gönder.
                                            if (reminderEnabled && appointment?.id) {
                                                items.push(
                                                    <Menu.Item
                                                        key="send-reminder"
                                                        onClick={() => ApiService.Appointment.SendReminderNotification(appointment.id)}
                                                    >
                                                        {t('Send appointment reminder notification')}
                                                    </Menu.Item>
                                                );
                                            }
                                            if (items.length === 0) return null;
                                            return (
                                                <Dropdown overlay={<Menu>{items}</Menu>} trigger={['click']} placement="bottomRight">
                                                    <Button size="small" type="text" icon={<DownOutlined />} />
                                                </Dropdown>
                                            );
                                        })()
                                    }
                                </div>
                            </Form.Item>
                        </div>
                        {!enabizConfigEnabled && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginBottom: 4, padding: '3px 10px' }}
                                message={<span style={{ fontSize: 11 }}>{t('E-Nabız sending appears to be disabled for the institution / unit / appointment type. Please inform IT before sending.')}</span>}
                            />
                        )}
                    </Col>
                    <Col xs={12}>
                    </Col>
                </Row>
            </Form>
            </Card>
            {/* Ayrı kart başlığı yok: bölüm ikonu sekme çubuğunun solunda, sekmeler başlık görevi görür */}
            <Card
                className="accent-left"
                style={{
                    backgroundColor: '#f5f5f5',
                    '--accent-color': operationsCardAccent,
                    ...cardCommon,
                }}
                bodyStyle={{ background: '#f5f5f5', padding: screens.md ? '4px 24px 12px' : '4px 12px 12px' }}
            >
                {/* Sağdaki ek alan (işlem seçici) sekmelerin bittiği yerden sağ kenara kadar uzar.
                    Sekme çubuğu hiç kaydırılmaz/kırpılmaz: dar alanda daralması gereken taraf işlem seçicidir. */}
                <style>{`
                    .appointment-operations-tabs > .ant-tabs-nav > .ant-tabs-nav-wrap { flex: none; overflow: visible; }
                    .appointment-operations-tabs > .ant-tabs-nav > .ant-tabs-nav-wrap .ant-tabs-nav-list { transform: none !important; }
                    .appointment-operations-tabs > .ant-tabs-nav .ant-tabs-nav-operations { display: none !important; }
                    /* rc-tabs sekme alanını "kap genişliği - ek alan" diye ölçüyor: ek alan sınırlanmazsa
                       sekmeler kaydırma moduna düşüyor. Sekmelere sabit pay ayrılır, daralan taraf seçici olur. */
                    .appointment-operations-tabs > .ant-tabs-nav > .ant-tabs-extra-content:last-child { flex: 1 1 auto; min-width: 0; max-width: calc(100% - 360px); padding-left: 16px; }
                `}</style>
                <div style={{ alignItems: 'start', gap: 8, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                        <Tabs
                            className={'appointment-operations-tabs'}
                            size={'small'}
                            tabPosition={'top'}
                            tabBarStyle={{ marginBottom: 8 }}
                            activeKey={operationsTab}
                            onChange={setOperationsTab}
                            tabBarExtraContent={{
                                left: (
                                    <FormOutlined style={{ color: operationsCardAccent, marginRight: 16 }} />
                                ),
                                // İşlemler: işlem seçici; Ekstra: ekleme formu. İkisi de hep mount'lu kalır (form ref'i / seçici durumu korunur)
                                right: screens.md ? (
                                    <>
                                        <div style={{ display: operationsTab === 'operations' ? 'block' : 'none' }}>
                                            <Select
                                                style={{ width: '100%' }}
                                                size={'small'}
                                                id={'operation-id-list'}
                                                autoClearSearchValue={true}
                                                placeholder={t('Select Operation')}
                                                disabled={
                                                    Helper.Date.isBeforeToday(appointment.startDate) &&
                                                    !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                                }
                                                showSearch={true}
                                                filterOption={(input, option) => {
                                                    return Helper.Text.normalizeText(option.children.toString()).includes(Helper.Text.normalizeText(input))
                                                }}
                                                mode={'multiple'}
                                                autoClearSearchValue={false}
                                                value={appointment?.appointmentOperations?.map(x => x.operationId)}
                                                onSelect={onSelectOperation}
                                                open={operationSelectOpen}
                                                onDropdownVisibleChange={(visible) => {
                                                    setOperationSelectOpen(visible)
                                                    if (!visible) setOperationSelectSearch('')
                                                }}
                                                onSearch={setOperationSelectSearch}
                                                // Eklenen işlemler hemen altındaki listede zaten görünüyor: kutuda etiket olarak
                                                // tekrar edilmez, kutu yalnız arama/ekleme alanıdır (seçililer açılır listede işaretli).
                                                maxTagCount={OPERATION_CARDS_ON_DESKTOP ? 0 : undefined}
                                                maxTagPlaceholder={() => operationSelectSearch ? null : t('Select Operation')}
                                                onInputKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        setTimeout(() => {
                                                            setOperationSelectOpen(false);
                                                            if (e.target && typeof e.target.blur === 'function') {
                                                                e.target.blur();
                                                            }
                                                        }, 0);
                                                    }
                                                }}
                                                tagRender={(props) => props.value === undefined ? (
                                                    // maxTagCount=0 iken "kalanlar" öğesi: yer tutucu gibi görünsün.
                                                    <span style={{ color: '#bfbfbf', paddingInlineStart: 4 }}>{props.label}</span>
                                                ) : (
                                                    <Tag
                                                        closable={!isOperationLocked(appointment?.appointmentOperations?.find(x => x.operationId === props.value))}
                                                        style={{
                                                            marginRight: 3,
                                                            whiteSpace: 'normal',
                                                            textAlign: 'left',
                                                            wordBreak: 'break-word',
                                                            height: 'auto',
                                                            maxWidth: '100%',
                                                            display: 'inline-flex',
                                                            alignItems: 'center'
                                                        }}
                                                        onClose={(e) => {
                                                            const target = appointment?.appointmentOperations?.find(x => x.operationId === props.value);
                                                            if ((Helper.Date.isBeforeToday(appointment.startDate) && !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)) || isOperationLocked(target)) {
                                                                e.preventDefault();
                                                                return;
                                                            }
                                                            const ops = appointment?.appointmentOperations?.filter(x => x.operationId !== props.value);
                                                            setAppointment({ ...appointment, appointmentOperations: ops });
                                                            formRef.current.setFieldsValue({
                                                                endDate: calcEndDate(ops, formRef.current.getFieldsValue().startDate)
                                                            });
                                                        }}
                                                    >
                                                        {props.label}
                                                    </Tag>
                                                )}
                                            >
                                                {operationList.map(x => (
                                                    <Select.Option key={x.id} value={x.id}>
                                                        {`${x.name} - ${x.processMinute} ${t('minute')}`}
                                                    </Select.Option>
                                                ))}
                                            </Select>
                                        </div>
                                        <div style={{ display: operationsTab === 'extra' ? 'flex' : 'none', justifyContent: 'flex-end' }}>
                                            {extraOperationForm}
                                        </div>
                                    </>
                                ) : null
                            }}
                        >
                            <Tabs.TabPane
                                tab={t('Operations')}
                                key={'operations'}
                            >
                {/* Farklı bir birime kayıtlı teklif varsa ufak uyarı */}
                {!hasQuote && otherUnitQuote && (
                    <div style={{
                        padding: '6px 10px',
                        background: '#fafafa',
                        border: '1px dashed #d9d9d9',
                        borderRadius: 4,
                        marginBottom: 8,
                        fontSize: 12,
                        color: '#8c8c8c',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        flexWrap: 'wrap',
                    }}>
                        <span>ℹ️</span>
                        <span>
                            {(otherUnitQuote.unitNames?.length || 0) > 1
                                ? 'Hafızada farklı birimlere ait fiyat teklifi var'
                                : 'Hafızada farklı birime ait fiyat teklifi var'}
                            {otherUnitQuote.unitName && (
                                <> — <b style={{ color: '#595959' }}>
                                    {otherUnitQuote.firmName ? `${otherUnitQuote.firmName} / ` : ''}{otherUnitQuote.unitName}
                                </b></>
                            )}
                            {` (${otherUnitQuote.opCount} kalem)`}
                        </span>
                    </div>
                )}
                {/* --- AKTARILACAK FİYAT TEKLİFİ BANNERI --- */}
                {hasQuote && (() => {
                    const quote = PriceQuoteStorage.getForUnit(appointment?.unit?.id);
                    if (!quote) return null;
                    const opCount = (quote?.operationList?.length || 0) + (quote?.otherOperations?.length || 0);
                    return (
                        <div style={{
                            padding: '10px 12px',
                            background: '#fffbe6',
                            border: '1px solid #ffe58f',
                            borderRadius: 6,
                            marginBottom: 12,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap'
                        }}>
                            <span style={{ fontSize: 18 }}>💾</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 500, fontSize: 13 }}>
                                    Hafızada fiyat teklifi var ({opCount} kalem)
                                </div>
                                <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                                    Bu randevuya aktarmak için bir seçim yapın
                                </div>
                            </div>
                            <Button
                                size="middle"
                                type="primary"
                                icon={<ImportOutlined />}
                                onClick={() => transferQuote('replace')}
                            >
                                Aktar
                            </Button>
                            <Button
                                size="middle"
                                onClick={discardQuote}
                            >
                                Sil
                            </Button>
                        </div>
                    );
                })()}
                                {/* Masaüstünde işlem seçici sekme çubuğunun sağında */}
                                {screens.md ? null : (
                                    <div>
                                        <Button
                                            block
                                            size="middle"
                                            icon={<PlusOutlined />}
                                            disabled={
                                                Helper.Date.isBeforeToday(appointment.startDate) &&
                                                !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)
                                            }
                                            onClick={openOperationPicker}
                                        >
                                            {t('Select Operation')}
                                            {appointment?.appointmentOperations?.length > 0 && ` (${appointment.appointmentOperations.length} seçili)`}
                                        </Button>

                                        {/* iOS mobil klavye primer — user-gesture context'inde focus alır,
                                            modal mount edildiğinde real input'a transfer edilir */}
                                        <input
                                            ref={operationKeyboardPrimerRef}
                                            type="text"
                                            inputMode="search"
                                            aria-hidden="true"
                                            tabIndex={-1}
                                            readOnly
                                            style={{
                                                position: 'fixed',
                                                top: 0,
                                                left: 0,
                                                width: 1,
                                                height: 1,
                                                opacity: 0,
                                                pointerEvents: 'none',
                                                border: 'none',
                                                background: 'transparent',
                                                zIndex: -1,
                                            }}
                                        />

                                        <Modal
                                            title={(
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingRight: 24 }}>
                                                    <span>{t('Select Operation')}</span>
                                                    <Tag color="blue" style={{ margin: 0 }}>
                                                        {appointment?.appointmentOperations?.length || 0} seçili
                                                    </Tag>
                                                </div>
                                            )}
                                            open={operationPickerOpen}
                                            onCancel={() => setOperationPickerOpen(false)}
                                            footer={null}
                                            width="100%"
                                            style={{ top: 0, maxWidth: '100vw', margin: 0, padding: 0 }}
                                            bodyStyle={{
                                                padding: 12,
                                                height: 'calc(100vh - 110px)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                overflow: 'hidden'
                                            }}
                                            destroyOnClose
                                        >
                                            <Input
                                                ref={operationPickerSearchRef}
                                                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                                                placeholder={t('Search')}
                                                size="middle"
                                                value={operationPickerSearch}
                                                onChange={(e) => setOperationPickerSearch(e.target.value)}
                                                allowClear
                                                autoFocus
                                                style={{ marginBottom: 8 }}
                                            />
                                            <div style={{
                                                flex: 1,
                                                overflowY: 'auto',
                                                border: '1px solid #f0f0f0',
                                                borderRadius: 4,
                                                marginBottom: 8
                                            }}>
                                                {operationList
                                                    .filter(x => !operationPickerSearch || Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(operationPickerSearch)))
                                                    .map(item => {
                                                        const selected = appointment?.appointmentOperations?.some(x => x.operationId === item.id);
                                                        return (
                                                            <div
                                                                key={item.id}
                                                                onClick={() => {
                                                                    if (selected) {
                                                                        // Kayıtlı işlem ve silme yetkisi yoksa kaldırılamaz.
                                                                        const target = appointment.appointmentOperations.find(x => x.operationId === item.id);
                                                                        if (isOperationLocked(target)) {
                                                                            return;
                                                                        }
                                                                        const ops = appointment.appointmentOperations.filter(x => x.operationId !== item.id);
                                                                        setAppointment({ ...appointment, appointmentOperations: ops });
                                                                        formRef.current.setFieldsValue({
                                                                            endDate: calcEndDate(ops, formRef.current.getFieldsValue().startDate)
                                                                        });
                                                                    } else {
                                                                        onSelectOperation(item.id);
                                                                    }
                                                                }}
                                                                style={{
                                                                    padding: '12px 10px',
                                                                    borderBottom: '1px solid #f5f5f5',
                                                                    backgroundColor: selected ? '#e6f7ff' : 'white',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 10,
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <div style={{
                                                                    width: 22, height: 22,
                                                                    border: `2px solid ${selected ? '#1890ff' : '#d9d9d9'}`,
                                                                    borderRadius: 4,
                                                                    background: selected ? '#1890ff' : 'white',
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    color: 'white', fontSize: 14, lineHeight: 1, flexShrink: 0
                                                                }}>
                                                                    {selected && '✓'}
                                                                </div>
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                    <div style={{ fontWeight: selected ? 600 : 400, fontSize: 14, wordBreak: 'break-word' }}>
                                                                        {item.name}
                                                                    </div>
                                                                    <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>
                                                                        {item.processMinute} {t('minute')}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                {operationList.filter(x => !operationPickerSearch || Helper.Text.normalizeText(x.name).includes(Helper.Text.normalizeText(operationPickerSearch))).length === 0 && (
                                                    <Empty
                                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                        description={t('No Data')}
                                                        style={{ padding: 24 }}
                                                    />
                                                )}
                                            </div>
                                            <Button
                                                type="primary"
                                                block
                                                size="large"
                                                onClick={() => setOperationPickerOpen(false)}
                                            >
                                                Tamam ({appointment?.appointmentOperations?.length || 0})
                                            </Button>
                                        </Modal>
                                    </div>
                                )}

                                <div>
                                    {(OPERATION_CARDS_ON_DESKTOP || !screens.md) ? (screens.md ? renderCompactOperations() : renderMobileOperations()) : (
                                        <Table
                                            style={{ marginTop: 8 }}
                                            scroll={{ x: 600 }}
                                            size={'small'}
                                            dataSource={appointment.appointmentOperations}
                                            pagination={false}
                                            rowKey={'id'}
                                            columns={[
                                                {
                                                    key: 'name',
                                                    dataIndex: 'name',
                                                    title: t('Name'),
                                                    render: (text, row) => {
                                                        const ops = operationList.find(x => x.id === row.operationId)
                                                        if (ops && ops.subItems?.length > 0) {
                                                            return (
                                                                <div style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 8
                                                                }}>
                                                                    <Tooltip
                                                                        title={(
                                                                            <>
                                                                                {row?.subItems?.length === 0 && t('No Sub Operations')}
                                                                                <table>
                                                                                    {
                                                                                        row?.subItems?.map(subItem => (
                                                                                            <tr>
                                                                                                <td>{subItem.name}</td>
                                                                                                <td>
                                                                                                    <ColorDot />
                                                                                                </td>
                                                                                                <td>{Helper.Currency.format(subItem.price, { currency })}</td>
                                                                                            </tr>
                                                                                        ))
                                                                                    }
                                                                                </table>
                                                                            </>
                                                                        )}
                                                                    >
                                                                        <span style={{ color: '#262626', fontWeight: 500 }}>{row?.operation?.name}</span>
                                                                    </Tooltip>
                                                                    <Button
                                                                        icon={<SmallDashOutlined />}
                                                                        size={'small'}
                                                                        disabled={isOperationLocked(row)}
                                                                        onClick={() => {
                                                                            openModal(
                                                                                t(''),
                                                                                <AppointmentOperationSubItemsEdit
                                                                                    currency={currency}
                                                                                    subItems={ops.subItems}
                                                                                    selectedSubItems={row.subItems ?? []}
                                                                                    onSave={(items) => {
                                                                                        const ops = appointment.appointmentOperations.map(x => {
                                                                                            if (x.id === row.id) {
                                                                                                return {
                                                                                                    ...x,
                                                                                                    price: items.reduce((prev, val) => prev += val.price, 0),
                                                                                                    subItems: items
                                                                                                }
                                                                                            }
                                                                                            return x
                                                                                        })
                                                                                        setAppointment({
                                                                                            ...appointment,
                                                                                            appointmentOperations: ops
                                                                                        })
                                                                                        closeModal()
                                                                                    }}
                                                                                />,
                                                                                () => { }
                                                                            )
                                                                        }}
                                                                    />
                                                                </div>
                                                            )
                                                        }
                                                        return <span style={{ color: '#262626', fontWeight: 500 }}>{row?.operation?.name}</span>
                                                    }
                                                },
                                                {
                                                    key: 'accessionNumber',
                                                    dataIndex: 'accessionNumber',
                                                    title: (<>{t('Accession Number')} / <br /> {t('Process Minute')}</>),
                                                    width: 225,
                                                    render: (text, row) => (
                                                        <div>
                                                            <Input
                                                                style={{ width: '100%' }}
                                                                size={'small'}
                                                                disabled={isOperationLocked(row)}
                                                                value={text}
                                                                onChange={(e) => {
                                                                    updateOperationData(row.id, 'accessionNumber', e.target.value)
                                                                }}
                                                            />
                                                            <InputNumber
                                                                style={{ width: '100%' }}
                                                                size={'small'}
                                                                disabled={isOperationLocked(row)}
                                                                defaultValue={row?.processMinute}
                                                                value={row?.processMinute}
                                                                onChange={(val) => {
                                                                    updateOperationData(row.id, 'processMinute', val)
                                                                }}
                                                            />
                                                        </div>
                                                    )
                                                },
                                                {
                                                    key: 'newPrice',
                                                    dataIndex: 'newPrice',
                                                    width: 300,
                                                    title: (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                            {t('Patient Op. Price')}
                                                            <TitleCurrencySelector
                                                                value={currency}
                                                                disabled={Helper.Date.isBeforeToday(appointment.startDate) && !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)}
                                                                onChange={(e) => {
                                                                    updateCurrency(e)
                                                                }}
                                                            />
                                                        </div>
                                                    ),
                                                    render: (text, row) => {
                                                        const ops = operationList.find(x => x.id === row.operationId)
                                                        return (
                                                            <div>
                                                                <InputNumber
                                                                    // disabled={ops?.subItems?.length > 0}
                                                                    disabled={
                                                                        (Helper.Date.isBeforeToday(appointment.startDate) &&
                                                                        !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)) ||
                                                                        isOperationLocked(row)
                                                                    }
                                                                    style={{ width: '100%' }}
                                                                    size={'small'}
                                                                    prefix={Currency[appointment.currency]}
                                                                    value={row?.price}
                                                                    step={0.01}
                                                                    onChange={(newPrice) => {
                                                                        if (ops?.subItems?.length > 0) {
                                                                            const updatedOperations = appointment.appointmentOperations.map(op => {
                                                                                if (op.id === row.id) {
                                                                                    const subItemsPrice = op.subItems.reduce((sum, item) => sum + item.price, 0)
                                                                                    const ratio = newPrice / subItemsPrice

                                                                                    op.price = newPrice
                                                                                    op.subItems = op.subItems.map(sub => ({
                                                                                        ...sub,
                                                                                        price: parseFloat((sub.price * ratio).toFixed(2))
                                                                                    }))

                                                                                    // Alt işlemler toplamı ile yeni fiyat arasında ufak fark varsa kapat
                                                                                    const newSubItemsPrice = op.subItems.reduce((sum, item) => sum + item.price, 0)
                                                                                    const diff = newPrice - newSubItemsPrice
                                                                                    if (Math.abs(diff) > 0.01 && op.subItems.length > 0) {
                                                                                        op.subItems[0].price = parseFloat((op.subItems[0].price + diff).toFixed(2))
                                                                                    }

                                                                                    return { ...op }
                                                                                }
                                                                                return op
                                                                            })

                                                                            setAppointment({
                                                                                ...appointment,
                                                                                appointmentOperations: updatedOperations
                                                                            })
                                                                        } else {
                                                                            updateOperationData(row.id, 'price', newPrice)
                                                                        }
                                                                    }}

                                                                />
                                                                <table>
                                                                    <tr>
                                                                        <td style={{ width: 100 }}>
                                                                            <Typography.Text>
                                                                                <b>{t('Price')}</b>
                                                                            </Typography.Text>
                                                                        </td>
                                                                        <td>
                                                                            <Typography.Text strong>
                                                                                {Helper.Currency.format(row?.operation?.price)}
                                                                            </Typography.Text>
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td style={{ width: 100 }}>
                                                                            <Typography.Text>
                                                                                <b>{t('Institution')} / {t('Special')}</b>
                                                                            </Typography.Text>
                                                                        </td>
                                                                        <td>
                                                                            <div style={{ width: '100%', display: 'flex', gap: 4 }}>
                                                                                <Typography.Text>
                                                                                    {formatOptionalPrice(getInstitutionPrice(row?.operationId))}
                                                                                </Typography.Text>
                                                                                {' / '}
                                                                                <Typography.Text>
                                                                                    {formatOptionalPrice(getAppointmentTypePrice(row?.operationId))}
                                                                                </Typography.Text>
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                </table>
                                                            </div>
                                                        )
                                                    }
                                                },
                                                {
                                                    key: '#',
                                                    dataIndex: '#',
                                                    title: '#',
                                                    width: 50,
                                                    render: (text, row) => (
                                                        <Button
                                                            size={'small'}
                                                            danger
                                                            disabled={
                                                                (Helper.Date.isBeforeToday(appointment.startDate) &&
                                                                !hasPermission(PERMISSIONS.EDIT_PAST_APPOINTMENT)) ||
                                                                isOperationLocked(row)
                                                            }
                                                            onClick={() => {
                                                                setAppointment({
                                                                    ...appointment,
                                                                    appointmentOperations: appointment.appointmentOperations.filter(x => x.id !== row.id),
                                                                })
                                                            }}>
                                                            X
                                                        </Button>
                                                    )
                                                }
                                            ]}
                                            summary={() => getPaymentSummary(4)}
                                        />
                                    )}
                                </div>
                            </Tabs.TabPane>
                            <Tabs.TabPane
                                key={'extra'}
                                tab={t('Extra')}
                            >
                                <>
                                    {/* Masaüstünde ekleme formu sekme çubuğunun sağında */}
                                    {!screens.md && extraOperationForm}

                                    {!screens.md ? renderMobileExtraOperations() : (
                                        <Table
                                            size={'small'}
                                            rowKey={'id'}
                                            dataSource={appointment.appointmentOtherOperations}
                                            pagination={false}
                                            columns={[
                                                { key: 'name', dataIndex: 'name', title: t('Name') },
                                                {
                                                    key: 'price',
                                                    dataIndex: 'price',
                                                    title: t('Price'),
                                                    render: (text, row) => Helper.Currency.format(text, { currency })
                                                },
                                                {
                                                    key: '#',
                                                    dataIndex: '#',
                                                    title: t('#'),
                                                    render: (text, row) => <Button size={'small'}
                                                        onClick={() => deleteOtherOperation(row)}
                                                        danger={true}>X</Button>
                                                },
                                            ]}
                                            summary={() => getPaymentSummary(3)}
                                        />
                                    )}
                                </>
                            </Tabs.TabPane>
                            <Tabs.TabPane
                                key={'consumables'}
                                tab={`${t('Consumables')}${consumables.lines.length > 0 ? ` (${consumables.lines.length})` : ''}`}
                            >
                                <AppointmentConsumables
                                    state={consumables}
                                    locked={consumablesLocked}
                                    onEditConfirmation={editConsumableConfirmation}
                                />
                            </Tabs.TabPane>
                        </Tabs>
                    </div>

                </div>
                {/* Kurum notu Operations kartının en altında — kullanıcı işlemler bölümüyle
                    birlikte görsün */}
                <div
                    dangerouslySetInnerHTML={{ __html: appointment.institution.note ?? '------' }}
                    style={{ marginTop: 8 }}
                />
            </Card>

            {/* Mobilde sayfa kenar boşluğu yok (kartlar tam genişlik); düğmeler ekran kenarına yapışmasın */}
            {!screens.md && (
                <div style={{ display: 'flex', marginBottom: 16, gap: 8, flexWrap: 'wrap', padding: '0 12px' }}>
                    {renderActionButtons()}
                </div>
            )}

            {/* Randevu saati seçici — birimin günlük takvimi. Global modal yerine yerel Modal:
                genişlik/yükseklik takvime göre ayarlanabilsin ve zaten modal içinde açılan
                randevu kartlarında iç içe modal sırasına takılmasın. */}
            <Modal
                title={`${t('Select Appointment Time')} — ${appointment?.unit?.name ?? ''}`}
                open={slotPickerOpen}
                onCancel={() => setSlotPickerOpen(false)}
                footer={null}
                width={screens.md ? '80vw' : '96vw'}
                style={{ top: 16, maxWidth: 1280 }}
                styles={{ body: { padding: 8 } }}
                destroyOnClose
            >
                <AppointmentSlotPicker
                    unitId={appointment?.unit?.id}
                    unitName={appointment?.unit?.name}
                    date={appointment.startDate}
                    height={Math.max(320, Math.round(window.innerHeight * 0.62))}
                    onSelect={applyPickedSlot}
                />
            </Modal>
        </>
    )
}

const TitleCurrencySelector = ({ value, onChange, disabled, compact }) => {
    const [val, setVal] = useState(value)

    useEffect(() => {
        onChange(val)
    }, [val]);

    useEffect(() => {
        if (value !== val) {
            setVal(value)
        }
    }, [value]);

    return (
        <CurrencySelect
            placeholder={''}
            style={{ width: compact ? 80 : 100 }}
            disabled={disabled}
            size={'small'}
            value={val}
            onChange={(e) => setVal(e)}
        />
    )
}